import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { ModalController } from '@ionic/angular';
import moment from 'moment';

export interface DateRange {
  startDate: Date;
  endDate: Date;
  type: 'relative' | 'absolute';
  label?: string;
}

export interface PresetDateRange {
  label: string;
  type: 'relative' | 'absolute';
  period?: 'day' | 'week' | 'month' | 'quarter' | 'year';
  startIndex?: number;
  endIndex?: number;
}

@Component({
  selector: 'app-date-range-picker',
  templateUrl: './date-range-picker.component.html',
  styleUrls: ['./date-range-picker.component.scss'],
})
export class DateRangePickerComponent implements OnInit {
  @Input() initialDateRange?: DateRange;
  @Input() disabled: boolean = false;

  @Output() dateRangeChanged = new EventEmitter<DateRange>();
  @Output() cancelled = new EventEmitter<void>();

  currentDateRange: DateRange;
  selectedPresetIndex: number = -1;
  currentMonth: Date = new Date();
  nextMonth: Date = new Date();
  isSelectingStartDate: boolean = true;

  presetRanges: PresetDateRange[] = [
    { label: 'Today', type: 'relative', period: 'day', startIndex: 0, endIndex: 0 },
    { label: 'Yesterday', type: 'relative', period: 'day', startIndex: 1, endIndex: 1 },
    { label: 'Last 7 days', type: 'relative', period: 'day', startIndex: 7, endIndex: 1 },
    { label: 'Last 14 days', type: 'relative', period: 'day', startIndex: 14, endIndex: 1 },
    { label: 'Last 30 days', type: 'relative', period: 'day', startIndex: 30, endIndex: 1 },
    { label: 'This month', type: 'relative', period: 'month', startIndex: 0, endIndex: 0 },
    { label: 'Last month', type: 'relative', period: 'month', startIndex: 1, endIndex: 1 },
    { label: 'This Quarter', type: 'relative', period: 'quarter', startIndex: 0, endIndex: 0 },
    { label: 'Custom', type: 'absolute' },
  ];

  constructor(private modalCtrl: ModalController) {}

  ngOnInit() {
    this.initializeDateRange();
    this.setupCalendarMonths();
  }

  private initializeDateRange() {
    if (this.initialDateRange) {
      this.currentDateRange = { ...this.initialDateRange };
      // Find matching preset
      this.selectedPresetIndex = this.findMatchingPresetIndex(this.initialDateRange);
    } else {
      // Default to last 14 days
      const endDate = moment().endOf('day').toDate();
      const startDate = moment().subtract(13, 'days').startOf('day').toDate();

      this.currentDateRange = {
        startDate,
        endDate,
        type: 'relative',
        label: 'Last 14 days',
      };
      this.selectedPresetIndex = 3; // Last 14 days
    }
  }

  private findMatchingPresetIndex(dateRange: DateRange): number {
    // Check if the date range matches any of the preset ranges
    for (let i = 0; i < this.presetRanges.length - 1; i++) {
      // -1 to exclude Custom
      const preset = this.presetRanges[i];
      const presetRange = this.calculateRelativeDateRange(preset);

      if (this.areDateRangesEqual(dateRange, presetRange)) {
        return i;
      }
    }

    // If no match found, return Custom index
    return this.presetRanges.length - 1; // Custom is the last item
  }

  private areDateRangesEqual(range1: DateRange, range2: DateRange): boolean {
    const start1 = moment(range1.startDate).startOf('day');
    const end1 = moment(range1.endDate).endOf('day');
    const start2 = moment(range2.startDate).startOf('day');
    const end2 = moment(range2.endDate).endOf('day');

    return start1.isSame(start2) && end1.isSame(end2);
  }

  private setupCalendarMonths() {
    const today = new Date();
    this.currentMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    this.nextMonth = new Date(today.getFullYear(), today.getMonth() + 1, 1);
  }

  selectPresetRange(index: number) {
    this.selectedPresetIndex = index;
    const preset = this.presetRanges[index];

    if (preset.type === 'relative') {
      this.currentDateRange = this.calculateRelativeDateRange(preset);
    } else if (preset.label === 'Custom') {
      // For Custom, keep the current date range but mark it as custom
      this.currentDateRange.type = 'absolute';
      this.currentDateRange.label = 'Custom';
    }

    this.currentDateRange.label = preset.label;
    this.currentDateRange.type = preset.type;

    // Don't emit immediately - wait for Update button
    // this.dateRangeChanged.emit(this.currentDateRange);
  }

  private calculateRelativeDateRange(preset: PresetDateRange): DateRange {
    const endDate = moment().endOf('day');
    let startDate: moment.Moment;

    if (preset.period === 'day') {
      if (preset.startIndex === 0) {
        // Today
        startDate = moment().startOf('day');
      } else if (preset.endIndex === 0) {
        // This period (WTD, MTD, QTD, YTD)
        startDate = moment().startOf(preset.period as any);
      } else {
        // Last N days
        startDate = moment()
          .subtract(preset.startIndex - 1, 'days')
          .startOf('day');
      }
    } else if (preset.period === 'week') {
      if (preset.startIndex === 0) {
        startDate = moment().startOf('week');
      } else {
        startDate = moment().subtract(preset.startIndex, 'weeks').startOf('week');
      }
    } else if (preset.period === 'month') {
      if (preset.startIndex === 0) {
        startDate = moment().startOf('month');
      } else {
        startDate = moment().subtract(preset.startIndex, 'months').startOf('month');
      }
    } else if (preset.period === 'quarter') {
      if (preset.startIndex === 0) {
        startDate = moment().startOf('quarter');
      } else {
        startDate = moment().subtract(preset.startIndex, 'quarters').startOf('quarter');
      }
    } else if (preset.period === 'year') {
      if (preset.startIndex === 0) {
        startDate = moment().startOf('year');
      } else {
        startDate = moment().subtract(preset.startIndex, 'years').startOf('year');
      }
    } else {
      startDate = moment().startOf('day');
    }

    return {
      startDate: startDate.toDate(),
      endDate: endDate.toDate(),
      type: 'relative',
      label: preset.label,
    };
  }

  selectDate(date: Date) {
    if (this.isSelectingStartDate) {
      this.currentDateRange.startDate = moment(date).startOf('day').toDate();
    } else {
      this.currentDateRange.endDate = moment(date).endOf('day').toDate();
    }

    // Ensure start date is before end date
    if (this.currentDateRange.startDate > this.currentDateRange.endDate) {
      if (this.isSelectingStartDate) {
        this.currentDateRange.endDate = moment(date).endOf('day').toDate();
      } else {
        this.currentDateRange.endDate = this.currentDateRange.startDate;
        this.currentDateRange.startDate = moment(date).startOf('day').toDate();
      }
    }

    this.currentDateRange.type = 'absolute';
    this.currentDateRange.label = undefined;

    // Check if the new date range matches any preset
    this.selectedPresetIndex = this.findMatchingPresetIndex(this.currentDateRange);

    this.isSelectingStartDate = !this.isSelectingStartDate;
  }

  navigateMonth(direction: 'prev' | 'next') {
    if (direction === 'prev') {
      this.currentMonth = moment(this.currentMonth).subtract(1, 'month').toDate();
      this.nextMonth = moment(this.nextMonth).subtract(1, 'month').toDate();
    } else {
      this.currentMonth = moment(this.currentMonth).add(1, 'month').toDate();
      this.nextMonth = moment(this.nextMonth).add(1, 'month').toDate();
    }
  }

  getCalendarDays(month: Date): Date[] {
    const start = moment(month).startOf('month').startOf('week');
    const end = moment(month).endOf('month').endOf('week');
    const days: Date[] = [];

    let current = start.clone();
    while (current.isSameOrBefore(end)) {
      days.push(current.toDate());
      current.add(1, 'day');
    }

    return days;
  }

  isDateInRange(date: Date): boolean {
    const momentDate = moment(date);
    const start = moment(this.currentDateRange.startDate);
    const end = moment(this.currentDateRange.endDate);

    return momentDate.isBetween(start, end, 'day', '[]');
  }

  isStartDate(date: Date): boolean {
    return moment(date).isSame(moment(this.currentDateRange.startDate), 'day');
  }

  isEndDate(date: Date): boolean {
    return moment(date).isSame(moment(this.currentDateRange.endDate), 'day');
  }

  isCurrentMonth(date: Date, month: Date): boolean {
    return moment(date).isSame(month, 'month');
  }

  getDateRangeLabel(): string {
    const start = moment(this.currentDateRange.startDate).format('MMM DD, YYYY');
    const end = moment(this.currentDateRange.endDate).format('MMM DD, YYYY');
    return `${start} - ${end}`;
  }

  getFormattedDate(date: Date): string {
    return moment(date).format('YYYY-MM-DD');
  }

  onStartDateChange(event: any) {
    const newDate = moment(event.detail.value).startOf('day').toDate();

    // Validate that start date is not after end date
    if (newDate > this.currentDateRange.endDate) {
      this.currentDateRange.endDate = moment(newDate).endOf('day').toDate();
    }

    this.currentDateRange.startDate = newDate;
    this.currentDateRange.type = 'absolute';
    this.currentDateRange.label = undefined;

    // Check if the new date range matches any preset
    this.selectedPresetIndex = this.findMatchingPresetIndex(this.currentDateRange);
  }

  onEndDateChange(event: any) {
    const newDate = moment(event.detail.value).endOf('day').toDate();

    // Validate that end date is not before start date
    if (newDate < this.currentDateRange.startDate) {
      this.currentDateRange.startDate = moment(newDate).startOf('day').toDate();
    }

    this.currentDateRange.endDate = newDate;
    this.currentDateRange.type = 'absolute';
    this.currentDateRange.label = undefined;

    // Check if the new date range matches any preset
    this.selectedPresetIndex = this.findMatchingPresetIndex(this.currentDateRange);
  }

  update() {
    this.dateRangeChanged.emit(this.currentDateRange);
  }

  cancel() {
    this.cancelled.emit();
  }
}
